import * as React from 'react';

import { Action, Dispatch, bindActionCreators } from 'redux';
import { DefaultButton, DialogFooter, Label, Panel, PanelType, PrimaryButton, TextField } from '@fluentui/react';
import { Partner, PartnerAddress } from '../../redux/on-site-manager/partners/types';

import { HealAPI, VendorAPI } from '../../healApi';
import { ToastParams } from '../../redux/message/types';
import { connect } from 'react-redux';
import { toast } from '../../redux/message/actions';
import { AddressInput, SuggestAddress } from '../core/AddressInput';

export interface OwnProps {
  partner: Partner;
  address: PartnerAddress;
  close: (partnerAddress?: PartnerAddress) => void;
}

export interface StateProps {}

export interface DispatchProps {
  actions: {
    toast: (params: ToastParams) => void;
  };
}

type PartnerAddressCreateOrEditPanelProps = OwnProps & DispatchProps;

export interface PartnerAddressCreateOrEditPanelState {
  isNew: boolean;
  isSaving: boolean;
  address: PartnerAddress;
}

class Component extends React.Component<PartnerAddressCreateOrEditPanelProps, PartnerAddressCreateOrEditPanelState> {
  constructor(props: PartnerAddressCreateOrEditPanelProps) {
    super(props);
    this.state = {
      isNew: !props.address.id,
      isSaving: false,
      address: {
        ...props.address,
      },
    };
  }

  public render(): JSX.Element {
    const { address } = this.state;

    let addressComponent;
    if (this.state.isNew) {
      addressComponent = (
        <div className="partner-row flex flex-column">
          <Label required={true}>Campus Location</Label>
          <AddressInput onSelect={this.onAddressSelect} />
        </div>
      );
    } else {
      addressComponent = (
        <div className="partner-row">
          <TextField label="Campus Location" required={true} disabled={true} value={address.fullAddress} />
        </div>
      );
    }

    return (
      <Panel isOpen={true} type={PanelType.medium} headerText={this.state.isNew ? 'Add campus' : 'Edit campus'} isHiddenOnDismiss={true} onDismiss={() => this.close()}>
        <div>
          <div className="partner-row">
            <TextField
              label="Campus Name"
              required={true}
              disabled={!!address.id}
              value={address.publicCode}
              onChange={(ev, value) => this.setState({ address: { ...address, name: value! } })}
            />
          </div>
          {addressComponent}
          <div className="partner-row">
            <TextField label="Contact name" required={true} value={address.contactName} onChange={(ev, value) => this.setState({ address: { ...address, contactName: value! } })} />
          </div>
          <div className="partner-row">
            <TextField
              label="Contact email"
              required={true}
              value={address.contactEmail}
              onChange={(ev, value) => this.setState({ address: { ...address, contactEmail: value! } })}
            />
          </div>
          <div className="partner-row">
            <TextField
              label="Phone number"
              required={true}
              value={address.contactPhone}
              onChange={(ev, value) => this.setState({ address: { ...address, contactPhone: value! } })}
            />
          </div>
        </div>

        <DialogFooter>
          <PrimaryButton onClick={this.onSubmit} disabled={!this.canSubmit()}>
            {this.state.isNew ? 'Create' : 'Save'}
          </PrimaryButton>
          <DefaultButton onClick={() => this.close()}>Cancel</DefaultButton>
        </DialogFooter>
      </Panel>
    );
  }

  private onAddressSelect = (address?: SuggestAddress) => {
    if (address) {
      this.setState({ address: { ...this.state.address, ...address, streetAddress: address.address, fullAddress: address.address } });
    }
  };

  private canSubmit(): boolean {
    const { address } = this.state;
    if (
      !address.fullAddress ||
      !address.streetAddress ||
      !address.name ||
      !address.contactEmail ||
      !address.contactName ||
      !address.contactPhone ||
      !address.fullAddress.trim() ||
      !address.contactEmail.trim() ||
      !address.contactPhone.trim()
    ) {
      return false;
    }
    return true;
  }

  private onSubmit = () => {
    this.setState({ isSaving: true });
    if (this.state.address.latitude && this.state.address.longitude) {
      VendorAPI.fetchTimeZone(this.state.address.latitude, this.state.address.longitude)
        .then((timezone) => {
          const addressToSubmit = {
            ...this.state.address,
            timezone,
          };
          if (this.state.isNew) {
            addressToSubmit.publicCode = this.props.partner.name + '-' + this.state.address.publicCode;
            HealAPI.createPartnerAddress(this.props.partner.id!, addressToSubmit)
              .then((response) => {
                this.setState({ isSaving: false });
                if (response.ok && response.data) {
                  this.close(response.data);
                } else {
                  const content = (response.data || ({} as any)).description || 'Something went wrong.';
                  this.props.actions.toast({ type: 'error', title: 'Error', content });
                }
              })
              .catch((error) => {
                this.setState({ isSaving: false });
                this.props.actions.toast({ type: 'error', title: 'Error', content: error });
              });
          } else {
            HealAPI.editPartnerAddress(this.props.partner.id!, addressToSubmit)
              .then((response) => {
                this.setState({ isSaving: false });
                if (response.ok && response.data) {
                  this.close(response.data);
                } else {
                  const content = (response.data || ({} as any)).description || 'Something went wrong.';
                  this.props.actions.toast({ type: 'error', title: 'Error', content });
                }
              })
              .catch((error) => {
                this.setState({ isSaving: false });
                this.props.actions.toast({ type: 'error', title: 'Error', content: error });
              });
          }
        })
        .catch((error) => {
          this.setState({ isSaving: false });
          this.props.actions.toast({
            type: 'error',
            title: 'Error',
            content: error || "Couldn't retreive the timezone for this address",
          });
        });
    } else {
      this.setState({ isSaving: false });
      this.props.actions.toast({
        type: 'error',
        title: 'Error',
        content: "Couldn't retreive the timezone for this address",
      });
    }
  };

  private close = (partnerAddress?: PartnerAddress) => {
    this.props.close(partnerAddress);
  };
}

export function mapStateToProps(): StateProps {
  return {};
}

export function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return {
    actions: bindActionCreators({ toast }, dispatch),
  };
}

export const PartnerAddressCreateOrEditPanel = connect(mapStateToProps, mapDispatchToProps)(Component);
